import GameModel from "../../GameModel";
import Grid from "../grid/Grid";
import WorldGrid from "../grid/WorldGrid";
import PerformanceMetrics from "../performance/PerformanceMetrics";
import GridSave from "./GridSave";
import GridSaveMeta from "./GridSaveMeta";

export default class GridSaveManager extends GameModel {
    /**
     * 
     * @param {GridSave} gridSave 
     */
    constructor(gridSave) {
        super();
        
        /**
         * @constant
         * @type {Map}
         */
        this.metadataByKey = new Map();

        /**
         * @constant
         * @type {GridSave}
         */
        this.gridSave = gridSave;

        /**
         * @constant
         * @type {number}
         */
        this.maxSizeThreshold = 50;

        /**
         * @constant
         * @type {number}
         */
        this.farColThreshold = 8;
        /**
         * @constant
         * @type {number}
         */
        this.farRowThreshold = 6;
    }

    /**
     * @param {WorldGrid} worldGrid 
     * @returns {Object}
     */
    generateGridStateForExport(worldGrid) {

        const worldGridState = {
            grids: []
        };

        const grids = worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const gridSave = this.gridSave.generateGridStateForExport(grids[i]);            
            worldGridState.grids.push(gridSave);
        }

        return worldGridState;
    }

    /**
     * @param {WorldGrid} worldGrid 
     * @param {Object} worldGridState 
     * @returns {boolean}
     */
    importGridState(worldGrid, worldGridState) {
        // The worldGridState looks like this:
        //
        // const worldGridState = {
        //     grids: [{grid state}, {grid state}, {grid state}, ...] // 9 elements. 3x3 array
        // };

        if (!worldGridState) {
            console.log('GridSaveManager.importGridState() No world grid state');
            return false;
        }

        if (!worldGridState.grids || worldGridState.grids.length < worldGrid.grids.length) {
            console.log('GridSaveManager.importGridState() Invalid grids array.');
            return false;
        }

        const grids = worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            const gridState = worldGridState.grids[i];

            // Note: importGridState will update the origin of the grid and the origins of all the zones/regions/tiles as well.
            if (!this.gridSave.importGridState(grid, gridState)) {
                console.log('GridSaveManager.importGridState() Failed to import grid index: ' + i);
                return false;
            }

            // Each gridState looks like this:
            // {
            //   key: 'asdfads',
            //   origin: {x: 123, y: 123},
            //   state: {info}
            // }

            const gridKey = gridState.key;

            // Update our internal representation of the grid states.
            if (!this.metadataByKey.has(gridKey)) {
                const metadata = new GridSaveMeta(gridKey, grid.getGridCol(), grid.getGridRow());
                this.metadataByKey.set(gridKey, metadata);
            }
            else {
                // If this shows up in the logs, then we are not clearing out old data before importing new stuff.
                console.log('GridSaveManager.importGridState() NOT UPDATING METADATA FOR KEY BECAUSE IT WAS ALREADY THERE.');
            }
        }

        return true;
    }

    /**
     * @param {GridSaveMeta} gridSaveMeta 
     */
    addGridSaveMetaFromSave(gridSaveMeta) {
        if (this.metadataByKey.has(gridSaveMeta.gridKey)) {
            console.log('GridSaveManager.addGridSaveMetaFromSave() added duplicate! key=' + gridSaveMeta.gridKey);
            console.trace();
            return;
        }

        this.metadataByKey.set(gridSaveMeta.gridKey, gridSaveMeta);
    }

    /**
     * @param {Grid} grid 
     */
    saveGridState(grid) {
        if (!grid) {
            return;
        }
        if (grid.gridChangeCount === 0) {
            //console.log('GridSaveManager.saveGridState() Grid change count is 0.');
            return;
        }

        const gridKey = this.gridSave.getGridKey(grid);

        PerformanceMetrics.startTime('SaveGrid');

        if (this.gridSave.saveGridState(grid)) {
            // If the meta is not already in our map, add it.
            if (!this.metadataByKey.has(gridKey)) {
                const metadata = new GridSaveMeta(gridKey, grid.getGridCol(), grid.getGridRow());
                this.metadataByKey.set(gridKey, metadata);
            }
        }
        else {
            console.log('GridSaveManager.saveGridState() Failed to save grid state. key=' + gridKey);
        }
        PerformanceMetrics.endTime('SaveGrid');
    }

    deleteAllGrids() {
        const gridKeys = [];

        for (const [gridKey] of this.metadataByKey.entries()) {
            // Probably don't want to modify the collection while we are iterating over it.
            gridKeys.push(gridKey);
        }

        for (let i = 0; i < gridKeys.length; i++) {
            this.removeGridState(gridKeys[i]);
        }
    }

    /**
     * Removes the save state for the grid.
     * @param {string} gridKey 
     */
    removeGridState(gridKey) {

        // Remove it from local storage
        this.gridSave.removeGridState(gridKey);

        // Remove it from the map
        this.metadataByKey.delete(gridKey);

        //console.log('GridSaveManager.removeGridState() removed metadata. key=' + gridKey + ', size=' + this.metadataByKey.size);
    }

    /**
     * @param {Grid} grid 
     * @returns {boolean} True if the grid has save data, false otherwise.
     */
    loadGridState(grid) {
        const gridKey = this.gridSave.getGridKey(grid);

        const saveExists = this.metadataByKey.has(gridKey);
        if (!saveExists) {
            //console.log('GridSaveManager.loadGridState() save does not exist.  gridKey=' + gridKey);
            return false;
        }

        PerformanceMetrics.startTime('LoadGrid');
        const loadResult = this.gridSave.loadGridState(grid);
        PerformanceMetrics.endTime('LoadGrid');

        if (!loadResult) {
            console.log('GridSaveManager.loadGridState() failed to load grid state. gridKey=' + gridKey);
            return false;
        }

        return true;
    }

    /**
     * @param {Grid} centerGrid The grid at the center of projection.
     * @param {Grid} crewGrid  The grid where the party is.
     */
    clearDistantGrids(centerGrid, crewGrid) {
        const col1 = centerGrid.getGridCol();
        const row1 = centerGrid.getGridRow();

        const distantKeys = [];

        if (centerGrid === crewGrid) {
            for (const [gridKey, gridMeta] of this.metadataByKey.entries()) {
                if (this.isDistant(col1, row1, gridMeta)) {
                    distantKeys.push(gridKey);
                }
            }
        }
        else {
            const col2 = crewGrid.getGridCol();
            const row2 = crewGrid.getGridRow();

            for (const [gridKey, gridMeta] of this.metadataByKey.entries()) {
                if (this.isDistant(col1, row1, gridMeta) && this.isDistant(col2, row2, gridMeta)) {
                    distantKeys.push(gridKey);
                }
            }
        }


        //const sizeBefore = this.metadataByKey.size;

        for (let i = 0; i < distantKeys.length; i++) {
            this.removeGridState(distantKeys[i]);
        }
    }

    /**
     * @private
     * @param {number} col 
     * @param {number} row 
     * @param {GridSaveMeta} gridMeta 
     * @returns {boolean}
     */
    isDistant(col, row, gridMeta) {
        return Math.abs(col - gridMeta.gridCol) > this.farColThreshold || Math.abs(row - gridMeta.gridRow) > this.farRowThreshold;
    }
    
    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.deleteAllGrids();
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }
}